import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { endOfWeek, format, startOfDay, startOfWeek } from 'date-fns';

import { FiWatch, FiLoader } from 'react-icons/fi';
import SportIcon from '@/app/activities/SportIcon';
import { TimePeriod } from '@/utils/prismaEnums';
import type { Workout } from '@trainme/db/client';
import { WorkoutChart } from '@/app/workouts/WorkoutChart';
import { WorkoutTooltip } from '@/app/components/WorkoutTooltip';
import type { WorkoutWithRelations } from '@/utils/types';
import { cn } from '@/utils/uiUtils';
import { formatDistance } from '@/utils/distanceUtils'; // Import formatDistance
import { toast } from 'react-toastify';
import { trpc } from '@/app/api/trpc/client';
import { unescapeHtmlEntities } from '@/utils/stringUtils';
import { useAnalysisStore } from '@/app/stores/analysisStore';
import { useWorkoutStore } from '@/app/stores/workoutStore';
import { useGarminSyncButton } from '@/app/workouts/hooks/useGarminSyncButton';
import { useUnitSystem } from '@/app/hooks/useUnitSystem';

export interface WorkoutPlannerContent {
  overview?: string;
  chatResponse?: string;
  workouts: Workout[];
  intro?: string;
}

interface WorkoutPlannerProps {
  content: WorkoutPlannerContent;
}

export const WorkoutPlanner = ({ content }: WorkoutPlannerProps) => {
  const { intro } = content;
  const initialWorkouts = content.workouts; // Store initial workouts
  const chatResponse = unescapeHtmlEntities(
    content.overview || content.chatResponse || ''
  );
  const {
    mergeWorkouts,
    removeWorkoutsByDateAndSport,
    removeWorkoutsByDateRange,
    workouts: workoutsMapFromStore, // Get workouts map from the store
  } = useWorkoutStore();
  const workoutsFromStore = Array.from(workoutsMapFromStore.values()); // Convert Map to array
  const { mergeAnalyses } = useAnalysisStore();
  const unitSystem = useUnitSystem();
  const utils = trpc.useUtils();
  const isBulkAddingRef = useRef(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const hasAutoSyncedRef = useRef(false);

  const { mutateAsync: upsertWorkout } = trpc.workouts.upsert.useMutation({
    onSuccess: async (response) => {
      // Only show toast if NOT in bulk mode
      // Check the ref at the time the callback executes
      if (!isBulkAddingRef.current) {
        toast.success('Workout added to calendar.');
      }

      // Add the workout directly to the Zustand store for immediate UI update
      if (response?.data) {
        mergeWorkouts([response.data]);
      }

      // Add the analyses from auto-match to the store (already in DB, just update store)
      if (
        response &&
        'analyses' in response &&
        Array.isArray(response.analyses) &&
        response.analyses.length > 0
      ) {
        mergeAnalyses(response.analyses);
      }

      // Only invalidate and refetch if not in bulk mode (bulk mode will handle at the end)
      if (!isBulkAddingRef.current) {
        // Refetch the affected week (don't invalidate all queries - that causes all weeks to refresh)
        if (response?.data?.date) {
          const workoutDate =
            response.data.date instanceof Date
              ? response.data.date
              : new Date(response.data.date);
          const weekStart = startOfWeek(workoutDate, { weekStartsOn: 1 });
          const weekEnd = endOfWeek(workoutDate, { weekStartsOn: 1 });

          // Remove workouts in this date range from store before refetching
          removeWorkoutsByDateRange({ start: weekStart, end: weekEnd });

          const result = await utils.workouts.getMany.fetch({
            filter: {
              date: {
                gte: weekStart,
                lt: weekEnd,
              },
            },
          });

          // Merge fetched workouts to ensure UI reflects latest state
          if (result.workouts && result.workouts.length > 0) {
            const normalizedWorkouts: WorkoutWithRelations[] =
              result.workouts.map((workout) => ({
                ...workout,
                analysis: workout.analysis ?? undefined,
              }));
            mergeWorkouts(normalizedWorkouts);
          }

          // Invalidate and refetch timely analysis queries for the affected day and week
          const normalizedDate = startOfDay(workoutDate);
          const normalizedWeekStart = startOfDay(weekStart);
          await Promise.all([
            utils.analysis.getTimelyAnalysis.invalidate({
              period: TimePeriod.DAILY,
              startDate: normalizedDate,
            }),
            utils.analysis.getTimelyAnalysis.invalidate({
              period: TimePeriod.WEEKLY,
              startDate: normalizedWeekStart,
            }),
          ]);
          // Explicitly fetch to ensure UI updates
          await Promise.all([
            utils.analysis.getTimelyAnalysis.fetch({
              period: TimePeriod.DAILY,
              startDate: normalizedDate,
            }),
            utils.analysis.getTimelyAnalysis.fetch({
              period: TimePeriod.WEEKLY,
              startDate: normalizedWeekStart,
            }),
          ]);
        }

        // Only invalidate analysis queries (not workout queries - we've already refetched what we need)
        utils.analysis.getMany.invalidate();
      }
    },
    onError: (error, _variables) => {
      // Only show error toast if not in bulk mode (bulk mode will show error at the end)
      if (!isBulkAddingRef.current) {
        toast.error('Failed to create workout: ' + error);
      }
    },
  });

  const syncWorkouts = useCallback(
    async (isAutoSync: boolean = false, syncGarmin: boolean = false) => {
      setIsSyncing(true);
      isBulkAddingRef.current = true;

      try {
        const needsUpsert = initialWorkouts.some((w) => !w.id);

        if (needsUpsert && !isAutoSync) {
          // Changed this condition
          initialWorkouts.forEach((workout) => {
            if (workout.date && workout.sportType) {
              const workoutDate =
                workout.date instanceof Date
                  ? workout.date
                  : new Date(workout.date);
              removeWorkoutsByDateAndSport(workoutDate, workout.sportType);
            }
          });

          await Promise.all(
            initialWorkouts.map((workout) =>
              upsertWorkout({
                workout: {
                  ...workout,
                  updatedAt: workout.updatedAt
                    ? new Date(workout.updatedAt)
                    : new Date(),
                  createdAt: workout.createdAt
                    ? new Date(workout.createdAt)
                    : new Date(),
                  duration: Math.round(
                    workout.duration ? workout.duration * 60 : 0
                  ),
                  distance: Math.round(
                    workout.distance ? workout.distance * 1000 : 0
                  ),
                },
                overrideExisting: true,
                syncGarmin,
              })
            )
          );

          if (!isAutoSync) {
            toast.success(
              `Synced ${initialWorkouts.length} workouts to Garmin`
            );
          }
        }

        // 2. Refetch Phase: Refresh the UI (Optimistic - errors here shouldn't fail the sync toast)
        try {
          const uniqueDates = new Set(
            initialWorkouts
              .map((w) => {
                if (!w.date) return null;
                const date = w.date instanceof Date ? w.date : new Date(w.date);
                return date.toISOString().split('T')[0];
              })
              .filter((d) => d !== null)
          );

          const uniqueDateArray = Array.from(uniqueDates);
          const BATCH_SIZE = 5;

          // Calculate unique week ranges
          const weekRanges = new Map<string, { start: Date; end: Date }>();
          uniqueDateArray.forEach((dateStr) => {
            const date = new Date(dateStr);
            const weekStart = startOfWeek(date, { weekStartsOn: 1 });
            const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
            const rangeKey = `${weekStart.toISOString()}-${weekEnd.toISOString()}`;
            if (!weekRanges.has(rangeKey)) {
              weekRanges.set(rangeKey, { start: weekStart, end: weekEnd });
            }
          });

          // Clear local store for affected ranges
          weekRanges.forEach((range) => {
            removeWorkoutsByDateRange(range);
          });

          // Refetch workouts in batches
          for (let i = 0; i < uniqueDateArray.length; i += BATCH_SIZE) {
            const batch = uniqueDateArray.slice(i, i + BATCH_SIZE);

            // Refetch weeks for this batch
            const batchWeekRanges = new Set<string>();
            batch.forEach((d) => {
              const date = new Date(d);
              const s = startOfWeek(date, { weekStartsOn: 1 }).toISOString();
              const e = endOfWeek(date, { weekStartsOn: 1 }).toISOString();
              batchWeekRanges.add(`${s}|${e}`);
            });

            await Promise.all(
              Array.from(batchWeekRanges).map(async (rangeStr) => {
                const [s, e] = rangeStr.split('|');
                const result = await utils.workouts.getMany.fetch({
                  filter: { date: { gte: new Date(s), lt: new Date(e) } },
                });
                if (result.workouts && result.workouts.length > 0) {
                  const normalizedWorkouts: WorkoutWithRelations[] =
                    result.workouts.map((workout) => ({
                      ...workout,
                      analysis: workout.analysis ?? undefined,
                    }));
                  mergeWorkouts(normalizedWorkouts);
                }
              })
            );

            // Refresh analysis for this batch
            const analysisPromises: Promise<unknown>[] = [];
            batch.forEach((dateStr) => {
              const date = new Date(dateStr);
              const normalizedDate = startOfDay(date);
              const weekStart = startOfWeek(date, { weekStartsOn: 1 });
              const normalizedWeekStart = startOfDay(weekStart);

              analysisPromises.push(
                utils.analysis.getTimelyAnalysis.invalidate({
                  period: TimePeriod.DAILY,
                  startDate: normalizedDate,
                }),
                utils.analysis.getTimelyAnalysis.invalidate({
                  period: TimePeriod.WEEKLY,
                  startDate: normalizedWeekStart,
                }),
                utils.analysis.getTimelyAnalysis.fetch({
                  period: TimePeriod.DAILY,
                  startDate: normalizedDate,
                }),
                utils.analysis.getTimelyAnalysis.fetch({
                  period: TimePeriod.WEEKLY,
                  startDate: normalizedWeekStart,
                })
              );
            });
            await Promise.all(analysisPromises);
          }

          utils.analysis.getMany.invalidate();
        } catch (refetchError) {
          console.warn(
            'Sync succeeded but calendar refresh failed:',
            refetchError
          );
        }
      } catch (error) {
        if (!isAutoSync) {
          toast.error('Failed to add workouts to calendar: ' + error);
        }
      } finally {
        isBulkAddingRef.current = false;
        setIsSyncing(false);
      }
    },
    [
      initialWorkouts, // Use initialWorkouts here
      mergeWorkouts,
      removeWorkoutsByDateAndSport,
      removeWorkoutsByDateRange,
      upsertWorkout,
      utils.workouts.getMany,
      utils.analysis.getTimelyAnalysis,
      utils.analysis.getMany,
    ]
  );

  useEffect(() => {
    if (
      initialWorkouts &&
      initialWorkouts.length > 0 &&
      !hasAutoSyncedRef.current
    ) {
      hasAutoSyncedRef.current = true;
      syncWorkouts(true);
    }
  }, [initialWorkouts, syncWorkouts]);

  // Filter workouts from the store that match these identifiers
  // This ensures we're rendering workouts that have IDs if they exist in the store
  const workoutsToRender = initialWorkouts
    .map((initialWorkout) => {
      if (!initialWorkout.date) return null; // Skip workouts without a date

      const initialWorkoutDate = format(
        new Date(initialWorkout.date),
        'yyyy-MM-dd'
      );

      // Find the corresponding workout in the store (which should have an ID)
      const storedWorkout = workoutsFromStore.find(
        (sw) =>
          sw.date &&
          format(new Date(sw.date), 'yyyy-MM-dd') === initialWorkoutDate &&
          sw.name === initialWorkout.name &&
          sw.sportType === initialWorkout.sportType
      );

      // Use stored workout if found, otherwise fallback to initial workout (which won't have an ID)
      // This ensures if a workout is truly not in the store, it's still displayed but its button will be disabled
      return storedWorkout || initialWorkout;
    })
    .filter(Boolean) as WorkoutWithRelations[]; // Remove nulls and cast to WorkoutWithRelations[]

  return (
    <div id='workout-planner' className='leading-relaxed md:leading-normal'>
      {intro && (
        <div className='mb-4 text-slate-900 dark:text-slate-100 italic bg-emerald-50 dark:bg-emerald-950/30 p-3 rounded-lg border border-emerald-100 dark:border-emerald-900/50'>
          {unescapeHtmlEntities(intro)}
        </div>
      )}
      <div className='leading-relaxed md:leading-normal prose prose-sm max-w-none'>
        <div className='prose'>{chatResponse}</div>
      </div>
      {workoutsToRender && workoutsToRender.length > 0 && (
        <ul className='workouts my-4 pl-0'>
          {[...workoutsToRender] // Use workoutsToRender for mapping
            .sort((a, b) => {
              const aTime = a.date ? new Date(a.date).getTime() : 0;
              const bTime = b.date ? new Date(b.date).getTime() : 0;
              return aTime - bTime;
            })
            .map((workout: Workout, index: number, sorted) => {
              // Get the current workout's date string for comparison
              const currentDateStr = workout.date
                ? format(new Date(workout.date), 'yyyy-MM-dd')
                : null;

              // Get the previous workout's date string (if exists)
              const prevWorkout = index > 0 ? sorted[index - 1] : null;
              const prevDateStr = prevWorkout?.date
                ? format(new Date(prevWorkout.date), 'yyyy-MM-dd')
                : null;

              // Check if we need to add a separator (different day)
              const shouldAddSeparator =
                index > 0 &&
                currentDateStr &&
                prevDateStr &&
                currentDateStr !== prevDateStr;

              const {
                isSynced: isGarminSynced,
                isLoading: isGarminActionLoading,
                handleGarminAction,
                buttonTitle: garminButtonTitle,
                colorClassName: garminIconColorClassName,
              } = useGarminSyncButton({
                workout: workout as WorkoutWithRelations,
                onWorkoutUpdate: (updatedWorkout) => {
                  mergeWorkouts([updatedWorkout]);
                },
              });

              return (
                <Fragment
                  key={`workout-group-${index}-${workout.date || 'no-date'}-${workout.name || 'unnamed'}`}
                >
                  {index === 0 && (
                    <li
                      key={`separator-before-first`}
                      className='col-span-8 my-4'
                    >
                      <hr className='border-slate-300 dark:border-slate-700' />
                    </li>
                  )}
                  {shouldAddSeparator && (
                    <li
                      key={`separator-${currentDateStr}-${index}`}
                      className='col-span-8 my-4'
                    >
                      <hr className='border-slate-300 dark:border-slate-700' />
                    </li>
                  )}
                  <li
                    key={`workout-planner-${workout.name || 'unnamed'}-${workout.date || index}-${index}`}
                    className={cn(
                      'w-full grid grid-cols-8 items-center',
                      'hover:bg-slate-200 dark:hover:bg-slate-800 my-4',
                      'text-slate-900 dark:text-slate-100'
                    )}
                  >
                    <div className='text-slate-500 dark:text-slate-400 font-medium text-sm'>
                      {index + 1}
                    </div>
                    <div className='col-span-6 md:col-span-2 flex flex-row md:flex-col gap-4'>
                      <div className=''>
                        {workout.date
                          ? format(new Date(workout.date), 'MMM dd, EEE')
                          : 'No date'}
                      </div>
                      <div>
                        {workout.duration} min,{' '}
                        {formatDistance(
                          (workout.distance || 0) * 1000,
                          unitSystem
                        )}
                      </div>
                    </div>
                    <div
                      className={cn([
                        'col-span-8 md:col-span-5',
                        'relative',
                        'min-w-0',
                      ])}
                    >
                      <div
                        className={cn([
                          'card cursor-pointer flex-shrink-0 rounded-lg shadow-none',
                          'tracking-tight cursor-pointer',
                          'w-full max-w-full',
                        ])}
                      >
                        <WorkoutTooltip workout={workout}>
                          <div className='card-header flex justify-between items-center gap-2 p-0 relative'>
                            <SportIcon
                              type={workout?.sportType || ''}
                              withColor={true}
                              className='text-yellow-400 flex-shrink-0'
                            />
                            <div className='flex-grow min-w-0 whitespace-nowrap text-ellipsis overflow-hidden'>
                              {workout?.name}
                            </div>
                            {/* Garmin Sync Button */}
                            <button
                              type='button'
                              className={cn(
                                'inline-flex items-center justify-center rounded-full p-1 transition-opacity',
                                (!workout.id || isGarminActionLoading) &&
                                  'cursor-not-allowed opacity-50',
                                isGarminActionLoading &&
                                  'cursor-wait opacity-80'
                              )}
                              disabled={!workout.id || isGarminActionLoading}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGarminAction();
                              }}
                              title={garminButtonTitle}
                            >
                              <FiWatch
                                className={cn(
                                  isGarminActionLoading && 'animate-spin',
                                  garminIconColorClassName
                                )}
                              />
                              <span className='sr-only'>
                                {garminButtonTitle}
                              </span>
                            </button>
                            {/* End Garmin Sync Button */}
                          </div>
                        </WorkoutTooltip>
                        <div className='card-body p-0'>
                          {/* StepsChart's own root enforces min-h-20 (80px);
                              a shorter fixed height here causes the chart +
                              its x-axis tick labels to overflow past this
                              wrapper (no overflow clipping), which pushed
                              the below day separator up over the labels.
                              Match that floor so the wrapper's box actually
                              contains the full rendered chart. */}
                          <div className='w-full h-20 min-w-0'>
                            <WorkoutChart
                              createKey={`coach-${Math.random().toFixed(3)}`}
                              workout={workout || undefined}
                              bottomMarginOffset={3}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                </Fragment>
              );
            })}
        </ul>
      )}
      {isSyncing && (
        <div className='flex justify-end items-center gap-2 text-xs text-slate-500 dark:text-slate-400'>
          <span>Syncing to calendar...</span>
          <FiLoader className='animate-spin w-3 h-3' />
        </div>
      )}
    </div>
  );
};
